/**
 * Detects when a new service-worker (i.e. a fresh deploy) is available
 * and exposes an `applyUpdate()` trigger.
 *
 * How it works:
 *   1. On mount, and every 60 s while a Doserne page is open, we call
 *      `registration.update()` — this pings the server for a fresh
 *      `sw.js`. If the file's byte content differs from the one the
 *      browser is running, the browser installs the new SW in the
 *      background and puts it in the `waiting` state.
 *   2. We listen for `updatefound` + `statechange('installed')` so the
 *      hook flips `updateAvailable: true` the moment a fresh worker
 *      is ready.
 *   3. `applyUpdate()` posts `SKIP_WAITING` to the waiting worker,
 *      then reloads the page once the new one takes control.
 *
 * Also listens for `controllerchange` so browsers where the SW was
 * upgraded via a background tab still trigger a page reload here.
 */
import { useCallback, useEffect, useState } from "react";

const CHECK_INTERVAL_MS = 60_000;

export function useSwUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

    let cancelled = false;
    let intervalId;

    (async () => {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg || cancelled) return;
      setRegistration(reg);

      // Auto-apply helper — silently activate the waiting SW so the
      // controllerchange listener below reloads the page. Deferred by
      // 800 ms so a still-typing user doesn't lose keystrokes mid-flow.
      const autoApply = (waitingWorker) => {
        if (!waitingWorker) return;
        setUpdateAvailable(true);
        setTimeout(() => {
          try { waitingWorker.postMessage({ type: "SKIP_WAITING" }); } catch { /* silent */ }
        }, 800);
      };

      // Waiting worker already present (page loaded AFTER an install)
      if (reg.waiting) autoApply(reg.waiting);

      const onUpdateFound = () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            autoApply(nw);
          }
        });
      };
      reg.addEventListener("updatefound", onUpdateFound);

      // Periodic poll for a new sw.js on the server
      const check = () => reg.update().catch(() => { /* offline etc */ });
      check();
      intervalId = setInterval(check, CHECK_INTERVAL_MS);

      // Also re-check whenever the tab regains focus (typical PWA
      // usage pattern: user opens the icon → hook wakes → probe).
      const onFocus = () => check();
      window.addEventListener("focus", onFocus);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") check();
      });

      return () => {
        window.removeEventListener("focus", onFocus);
      };
    })();

    // When the active worker changes underneath us, reload once so the
    // whole app is on the new version. This is the trigger for the
    // *automatic* reload — no user action required.
    let reloaded = false;
    const onControllerChange = () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  const applyUpdate = useCallback(() => {
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    } else {
      // Fallback: just hard-reload, gives worst-case still-fresh HTML.
      window.location.reload();
    }
  }, [registration]);

  return { updateAvailable, applyUpdate };
}
